import { useInfiniteQuery } from 'react-query';
import { useContext, useEffect, useState } from 'react';
import {
  SearchContext,
  SearchContextInterface
} from '../../search-new/context/SearchContext';
import { isEqual } from '../../../helpers/isEqual';

import { useDispatch, useSelector } from 'react-redux';
import {
  setMapFilters,
  setMapSearchStates
} from '../../../redux/slices/mapSlice';
import { MapSearchFiltersTypes, ResultsType } from '../types';
import { useRouter } from 'next/router';
import { MapsApis } from '../../../new-lib/apis/maps.apis';

const useMapSearch = () => {
  const { asPath, locale } = useRouter();
  const dispatch = useDispatch();
  const { viewBounds, compoundViewBounds, resultsType, areaLoaded } =
    useSelector((state: any) => state.map, isEqual);
  const [allCompounds, setAllCompounds] = useState<any[]>([]);
  const [allAreas, setAllAreas] = useState<any[]>([]);
  const { searchFiltersState } = useContext(
    SearchContext
  ) as SearchContextInterface;
  const filters = useSelector((state: any) => state.map.filters, isEqual);
  const searchFiltersToMapFilters: any = {
    areas: asPath.includes('/nawy-now') && MapSearchFiltersTypes.areas,
    compounds: asPath.includes('/nawy-now') && MapSearchFiltersTypes.compounds,
    developers: MapSearchFiltersTypes.developers,
    property_types: MapSearchFiltersTypes.propertyTypes,
    amenities: MapSearchFiltersTypes.amenities,
    bedrooms: MapSearchFiltersTypes.bedrooms,
    bathrooms: MapSearchFiltersTypes.bathrooms,
    finishing: MapSearchFiltersTypes.finishing,
    min_price: MapSearchFiltersTypes.minPrice,
    max_price: MapSearchFiltersTypes.maxPrice,
    min_unit_area: MapSearchFiltersTypes.minUnitArea,
    max_unit_area: MapSearchFiltersTypes.maxUnitArea,
    delivery_date: MapSearchFiltersTypes.minReadyBy,
    delivery_years: MapSearchFiltersTypes.readyByYears,
    down_payment: MapSearchFiltersTypes.maxDownPayment,
    monthly_installments: MapSearchFiltersTypes.maxInstallments,
    installment_years: MapSearchFiltersTypes.installmentYears,
    sale_type: MapSearchFiltersTypes.saleType,
    nawy_now: MapSearchFiltersTypes.isNawyNow
  };

  const mapFiltersToNewKeys = () => {
    return Object.keys(searchFiltersState.searchFilters).reduce(
      (acc: any, key) => {
        const mappedKey = searchFiltersToMapFilters[key];
        const getValue = () => {
          if (mappedKey == MapSearchFiltersTypes.minReadyBy) {
            const oldVal =
              searchFiltersState.searchFilters[
                key as keyof typeof searchFiltersState.searchFilters
              ];
            if (oldVal) {
              if (typeof oldVal === 'string') {
                return new Date(oldVal).getFullYear();
              }
              return undefined;
            }
          } else {
            return searchFiltersState.searchFilters[
              key as keyof typeof searchFiltersState.searchFilters
            ];
          }
        };
        const value: any = getValue();
        if (
          mappedKey &&
          value &&
          (Array.isArray(value) ? value.length > 0 : true)
        ) {
          acc[mappedKey] = value;
        }
        return acc;
      },
      {}
    );
  };

  const isSahelMap = asPath.includes('sahel-map');
  const isNawyNowMap = asPath.includes('nawy-now');

  const {
    isFetching: compoundFetching,
    isError: compoundError,
    isLoading: compoundLoading
  } = useInfiniteQuery({
    queryKey: ['mapCompounds', compoundViewBounds, filters, areaLoaded],
    queryFn: () => {
      if (isSahelMap) {
        return MapsApis.getAllMapCompounds({
          areaIds: [3],
          ...mapFiltersToNewKeys()
        });
      }
      if (isNawyNowMap) {
        return MapsApis.getNawyNowMap(
          { ...compoundViewBounds, ...mapFiltersToNewKeys() },
          locale
        );
      }
      return MapsApis.getMapCompounds(
        { ...compoundViewBounds, ...mapFiltersToNewKeys() },
        locale
      );
    },
    enabled:
      resultsType === ResultsType.COMPOUNDS &&
      compoundViewBounds !== null &&
      areaLoaded,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    onSuccess: data => {
      const newCompounds = data.pages.flatMap(page => page);
      setAllCompounds(prevCompounds => {
        const uniqueCompounds = new Map();
        [...prevCompounds, ...newCompounds].forEach((compound: any) => {
          uniqueCompounds.set(compound.id, compound);
        });
        let uniqueCompoundsArray = Array.from(uniqueCompounds.values());
        if (uniqueCompoundsArray.length > 100) {
          uniqueCompoundsArray = uniqueCompoundsArray.slice(-100);
        }
        return uniqueCompoundsArray;
      });
    }
  });

  const {
    isFetching: areaFetching,
    isError: areaError,
    isLoading: areaLoading
  } = useInfiniteQuery({
    queryKey: ['mapAreas', viewBounds, filters],
    queryFn: () => {
      if (isNawyNowMap) {
        return MapsApis.getNawyNowMapAreas(
          { ...viewBounds, ...mapFiltersToNewKeys() },
          locale
        );
      }
      return MapsApis.getMapAreas(
        { ...viewBounds, ...mapFiltersToNewKeys() },
        locale
      );
    },
    enabled: resultsType === ResultsType.AREAS && viewBounds !== null,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    onSuccess: data => {
      const newAreas = data.pages.flatMap(page => page);
      setAllAreas(prevAreas => {
        const uniqueAreas = new Map();
        [...prevAreas, ...newAreas].forEach((area: any) => {
          uniqueAreas.set(area.id, area);
        });
        return Array.from(uniqueAreas.values());
      });
    }
  });

  useEffect(() => {
    dispatch(setMapFilters(mapFiltersToNewKeys()));
    setAllCompounds([]);
  }, [searchFiltersState]);
  useEffect(() => {
    dispatch(
      setMapSearchStates({
        isLoading: compoundLoading || areaLoading,
        isFetching: compoundFetching || areaFetching,
        isError: compoundError || areaError
      })
    );
  }, [
    areaFetching,
    areaError,
    areaLoading,
    compoundFetching,
    compoundError,
    compoundLoading
  ]);
  return {
    areas: allAreas,
    compounds: allCompounds,
    isFetching: compoundFetching || areaFetching,
    isLoading: compoundLoading || areaLoading,
    isError: compoundError || areaError
  };
};

export default useMapSearch;
